import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatPct(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function formatSigned(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const formatted = Math.abs(number).toLocaleString("ru-RU", {
    maximumFractionDigits: 0,
  });
  if (number > 0) return `+${formatted}`;
  if (number < 0) return `−${formatted}`;
  return formatted;
}

function formatDay(value) {
  if (!value) return "";
  const [, month, day] = String(value).slice(0, 10).split("-");
  return day && month ? `${day}.${month}` : value;
}

function eachIsoDay(from, to) {
  const days = [];
  if (!from || !to || from > to) return days;
  const current = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const day = String(current.getDate()).padStart(2, "0");
    days.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function barWidth(value, max) {
  const number = Number(value);
  if (!max || !Number.isFinite(number) || number <= 0) return 0;
  return Math.min(100, (number / max) * 100);
}

function MetricCard({ label, value, hint, tone }) {
  return (
    <article className={`dash-metric${tone ? ` is-${tone}` : ""}`}>
      <p className="dash-metric-label">{label}</p>
      <p className="dash-metric-value">{value}</p>
      {hint ? <p className="dash-metric-hint">{hint}</p> : null}
    </article>
  );
}

function ItemsTable({ title, items, totalLabel, total }) {
  if (!items?.length) return null;
  return (
    <section className="dash-panel">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        {total != null ? (
          <span className="dash-panel-total">{formatMoney(total)}</span>
        ) : null}
      </div>
      <div className="dash-table-wrap">
        <table className="dash-table">
          <tbody>
            {items.map((item) => (
              <tr key={item.field_name}>
                <td>{item.label}</td>
                <td className="is-num">{formatMoney(item.value)}</td>
              </tr>
            ))}
          </tbody>
          {total != null ? (
            <tfoot>
              <tr>
                <td>{totalLabel}</td>
                <td className="is-num">{formatMoney(total)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </section>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  return (
    <div className="dash-chart-tooltip">
      <p className="dash-chart-tooltip-date">{label}</p>
      <p>
        <strong>{value == null ? "нет отчёта" : formatMoney(value)}</strong>
      </p>
    </div>
  );
}

function DailyChart({ title, dateFrom, dateTo, series, dataKey, color }) {
  const byDate = Object.fromEntries(
    (series || []).map((item) => [item.date, item]),
  );
  const data = eachIsoDay(dateFrom, dateTo).map((date) => {
    const row = byDate[date];
    return {
      date,
      label: formatDay(date),
      value: row ? Number(row[dataKey]) || 0 : null,
    };
  });
  if (!data.length) return null;

  return (
    <section className="dash-panel dash-side-chart">
      <div className="dash-panel-head">
        <h2>{title}</h2>
      </div>
      <div className="dash-side-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={formatMoney}
              width={72}
            />
            <Tooltip content={<ChartTooltip />} />
            <Bar
              dataKey="value"
              fill={color}
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function DaysFlow({ dateFrom, dateTo, series }) {
  const byDate = Object.fromEntries(
    (series || []).map((item) => [item.date, item]),
  );
  const days = eachIsoDay(dateFrom, dateTo);
  if (!days.length) return null;

  const max = Math.max(
    0,
    ...days.map((date) => Number(byDate[date]?.income_day) || 0),
    ...days.map((date) => Number(byDate[date]?.expense_total) || 0),
  );
  const splitAt = Math.ceil(days.length / 2);
  const columns = [days.slice(0, splitAt), days.slice(splitAt)];

  return (
    <section className="dash-panel dash-days-panel">
      <div className="dash-panel-head">
        <h2>Приход и расход по дням</h2>
        <div className="dash-days-legend">
          <span className="is-in">приход</span>
          <span className="is-out">расход</span>
        </div>
      </div>
      <div className="dash-days">
        {columns.map((column, index) =>
          column.length ? (
            <div key={index} className="dash-days-col">
              {column.map((date) => {
                const row = byDate[date];
                const hasData = Boolean(row);
                const income = row?.income_day;
                const expense = row?.expense_total;
                return (
                  <div
                    key={date}
                    className={`dash-days-row${hasData ? "" : " is-empty"}`}
                  >
                    <div className="dash-days-date">{formatDay(date)}</div>
                    <div className="dash-days-bars">
                      <div className="dash-days-track" title="Приход">
                        <span
                          className="dash-days-fill is-in"
                          style={{ width: `${barWidth(income, max)}%` }}
                        />
                      </div>
                      <div className="dash-days-track" title="Расход">
                        <span
                          className="dash-days-fill is-out"
                          style={{ width: `${barWidth(expense, max)}%` }}
                        />
                      </div>
                    </div>
                    <div className="dash-days-amounts">
                      {hasData ? (
                        <>
                          <span className="is-in">
                            пришло {formatMoney(income)}
                          </span>
                          <span className="is-out">
                            ушло {formatMoney(expense)}
                          </span>
                        </>
                      ) : (
                        <span className="dash-days-nodata">нет отчёта</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null,
        )}
      </div>
    </section>
  );
}

const FinanceDashboard = ({ summary }) => {
  const revenue = summary.revenue || {};
  const cash = summary.cash || {};
  const credit = summary.credit || {};
  const deposit = summary.deposit || {};
  const restaurants = summary.restaurants || [];
  const showNetwork = summary.restaurant_id == null;
  const snapshotHint = summary.snapshot_date
    ? `на ${formatDay(summary.snapshot_date)}`
    : "";
  const forecastHint = summary.forecast_date
    ? `из отчёта ${formatDay(summary.forecast_date)}`
    : "";

  return (
    <div className="dash-finance">
      <section className="dash-metrics">
        <MetricCard
          label="Выручка за период"
          value={formatMoney(revenue.day)}
          hint="сумма по дням"
        />
        <MetricCard
          label="Выручка с начала месяца"
          value={formatMoney(revenue.month)}
          hint={snapshotHint}
        />
        <MetricCard
          label="План на месяц"
          value={formatMoney(revenue.plan_month)}
          hint={snapshotHint}
        />
        <MetricCard
          label="% выполнения плана месяца"
          value={formatPct(revenue.completion_pct)}
          hint={
            snapshotHint
              ? `выручка с 1-го числа / план на месяц, ${snapshotHint}`
              : "выручка с 1-го числа / план на месяц"
          }
          tone={Number(revenue.completion_pct) >= 100 ? "good" : ""}
        />
        <MetricCard
          label="Прогноз ТО"
          value={formatMoney(revenue.forecast)}
          hint={forecastHint}
        />
        <MetricCard
          label="Отклонение прогноза"
          value={formatSigned(revenue.forecast_deviation)}
          hint={forecastHint}
          tone={
            Number(revenue.forecast_deviation) < 0
              ? "warn"
              : Number(revenue.forecast_deviation) > 0
                ? "good"
                : ""
          }
        />
      </section>

      {showNetwork ? (
        <section className="dash-metrics">
          <MetricCard
            label="Приход"
            value={formatMoney(summary.income_day)}
            hint="сумма за период"
          />
          <MetricCard
            label="Расход"
            value={formatMoney(summary.expense_total)}
            hint="сумма за период"
          />
          <MetricCard
            label="Остаток на р/с"
            value={formatMoney(cash.day_end)}
            hint={snapshotHint}
          />
          <MetricCard
            label="Кредитный портфель"
            value={formatMoney(credit.portfolio)}
            hint={snapshotHint}
          />
          <MetricCard
            label="Депозит"
            value={formatMoney(deposit.amount)}
            hint={snapshotHint}
          />
          <MetricCard
            label="Товарные запасы"
            value={formatMoney(revenue.inventory)}
            hint={snapshotHint}
          />
        </section>
      ) : null}

      {showNetwork ? (
        <DaysFlow
          dateFrom={summary.date_from}
          dateTo={summary.date_to}
          series={summary.series}
        />
      ) : null}

      {showNetwork ? (
        <>
          <div className="dash-split dash-split-chart">
            <ItemsTable
              title="Доставка"
              items={summary.delivery}
              totalLabel="Итого"
              total={summary.delivery_total}
            />
            <DailyChart
              title="Доставка по дням"
              dateFrom={summary.date_from}
              dateTo={summary.date_to}
              series={summary.series}
              dataKey="delivery_total"
              color="#8aa0b8"
            />
          </div>
          <div className="dash-split dash-split-chart">
            <ItemsTable
              title="Расход"
              items={summary.expenses}
              totalLabel="Итого"
              total={summary.expense_total}
            />
            <DailyChart
              title="Расход по дням"
              dateFrom={summary.date_from}
              dateTo={summary.date_to}
              series={summary.series}
              dataKey="expense_total"
              color="#c4a574"
            />
          </div>
        </>
      ) : null}

      {restaurants.length > 0 ? (
        <section className="dash-panel">
          <div className="dash-panel-head">
            <h2>Рестораны</h2>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table dash-table-wide">
              <thead>
                <tr>
                  <th>Ресторан</th>
                  <th className="is-num">План</th>
                  <th className="is-num">Прогноз</th>
                  <th className="is-num">За период</th>
                  <th className="is-num">С начала месяца</th>
                  <th className="is-num">% плана</th>
                  <th className="is-num">Запасы</th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td className="is-num">{formatMoney(item.plan_month)}</td>
                    <td className="is-num">{formatMoney(item.forecast)}</td>
                    <td className="is-num">{formatMoney(item.day)}</td>
                    <td className="is-num">{formatMoney(item.month)}</td>
                    <td className="is-num">{formatPct(item.completion_pct)}</td>
                    <td className="is-num">{formatMoney(item.inventory)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {showNetwork ? (
        <div className="dash-split">
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Остатки на р/с</h2>
            </div>
            <dl className="dash-dl">
              <div>
                <dt>Начало месяца</dt>
                <dd>{formatMoney(cash.month_start)}</dd>
              </div>
              <div>
                <dt>Начало дня</dt>
                <dd>{formatMoney(cash.day_start)}</dd>
              </div>
              <div>
                <dt>Конец дня</dt>
                <dd>{formatMoney(cash.day_end)}</dd>
              </div>
            </dl>
          </section>
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Кредиты и депозит</h2>
            </div>
            <dl className="dash-dl">
              <div>
                <dt>Портфель</dt>
                <dd>{formatMoney(credit.portfolio)}</dd>
              </div>
              <div>
                <dt>Взято за месяц</dt>
                <dd>{formatMoney(credit.taken_month)}</dd>
              </div>
              <div>
                <dt>Погашено за месяц</dt>
                <dd>{formatMoney(credit.paid_month)}</dd>
              </div>
              <div>
                <dt>Депозит</dt>
                <dd>{formatMoney(deposit.amount)}</dd>
              </div>
              <div>
                <dt>% кэшбэк ФОТ</dt>
                <dd>{formatPct(summary.cashback_fot)}</dd>
              </div>
            </dl>
          </section>
        </div>
      ) : null}

      <section className="dash-legend">
        <h2>Как считаются цифры</h2>
        <p>
          Берём загрузки за выбранные даты
          {summary.loaded_dates?.length
            ? ` (есть отчёты за ${summary.loaded_dates
                .map(formatDay)
                .join(", ")})`
            : ""}
          . «Все рестораны» — сумма по точкам из файла, без итоговой строки
          Excel и без «Выходим на». Если выбрана одна точка, блоки сети
          (приход, расход, доставка, р/с, кредиты) скрываются: в файле они
          не разложены по ресторанам.
        </p>
        <dl>
          <div>
            <dt>Сумма за период</dt>
            <dd>
              Складываем дневные суммы из каждого отчёта: выручка за период,
              приход, расход, каналы доставки. Графики справа от доставки и
              расхода — сумма за каждый день периода. Полоски выше — приход
              и расход за дату, в одном масштабе.
            </dd>
          </div>
          <div>
            <dt>Срез на последний день</dt>
            <dd>
              План на месяц, выручка с начала месяца, товарные запасы,
              недельный ТО, остатки на р/с, кредиты и депозит не
              суммируем по дням — берём отчёт за{" "}
              {summary.snapshot_date
                ? formatDay(summary.snapshot_date)
                : "последний день периода с загрузкой"}
              . Иначе план и остатки задвоятся.
            </dd>
          </div>
          <div>
            <dt>Прогноз ТО и отклонение</dt>
            <dd>
              Всегда из самой свежей загрузки в базе
              {summary.forecast_date
                ? `, сейчас это ${formatDay(summary.forecast_date)}`
                : ""}
              , даже если на экране другой месяц. В файле это уже оценка
              наперёд, а не факт выбранных дней.
            </dd>
          </div>
          <div>
            <dt>% выполнения плана месяца</dt>
            <dd>
              Выручка с начала месяца ÷ план на месяц. В начале месяца
              цифра небольшая — в знаменателе план на все дни, а не на
              выбранный период. Проценты из Excel не складываем: для сети
              делим суммы, а не среднее по точкам.
            </dd>
          </div>
          <div>
            <dt>% от недельного ТО</dt>
            <dd>
              Товарные запасы ÷ недельный ТО на тот же срез. Тоже
              пересчитываем из сумм, без сложения процентов.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
};

export default FinanceDashboard;
